import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'

export interface Props {
  className?: string
  distance?: string
  description?: string
  image?: ImageProps
  isLazy?: boolean
  languageCode: string
  title?: string
}

export const Poi = memo(function Poi({
  className,
  distance,
  description,
  image,
  isLazy = false,
  title,
}: Props) {
  return (
    <Container className={className}>
      <Background>
        {image && isLazy ? <Image {...image} /> : <Image {...image} />}
      </Background>

      {title ? (
        <Wrapper dial={7} className="item-wrap">
          {distance ? <Distance>{distance}</Distance> : null}
          <Title>{title}</Title>

          {description ? (
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          ) : null}
        </Wrapper>
      ) : null}
    </Container>
  )
})

const Container = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  width: 100%;
  height: 100%;
`

const Background = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  &:before,
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark1, 0)},
      ${rgba(theme.colors.variants.neutralDark1, 0.4)}
    );
    mix-blend-mode: multiply;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
  }
`

const Wrapper = styled(FlexBox)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 3rem 3rem;
  z-index: 6;
  transition: all 0.6s 0.1s ease-in-out;
  text-align: left;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};

  @media (max-width: 1199px) {
    padding: 0 1.875rem 1.875rem;
  }
`

const Distance = styled.div`
  ${styles.label}

  margin-bottom: 0.75rem;
`

const Title = styled.div`
  ${styles.title.small}

  text-transform: none;
  margin-bottom: 0.75rem;
  margin-top: auto;

  @media (max-width: 767px) {
    margin-bottom: 0;
  }
`

const Description = styled.div`
  ${styles.description.light}

  color: ${({ theme }) => theme.colors.variants.neutralLight4};

  @media (max-width: 767px) {
    display: none;
  }
`
